import { Eye, EyeOff, Trash2, X } from "lucide-react";
import { useSvgDiagram2D } from "./SvgDiagram2DContext";
import { DebouncedInput } from "../reusable/DebouncedInput";
import { CheckboxField } from "../reusable/CheckboxField";
import { StrokeStyleControl } from "../reusable/StrokeStyleControl";
import { isMultiShapeSelection } from "./SvgDiagram2DMultiSelectPanel";

function num(v: string | number, fallback: number): number {
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) ? n : fallback;
}

/**
 * Floating inspector for the selected arc: edits its two endpoints, the bulge
 * (which side of the chord it curves to and how deep), and stroke style.
 * Rendered as an overlay inside the viewer (mirrors the line/ellipse
 * inspectors); closes itself when no arc is selected.
 */
export function SvgDiagram2DArcPanel() {
    const ctx = useSvgDiagram2D();
    const { doc, selectedArcIds, setSelectedArcId, updateArc, removeArc, pushSnapshot } =
        ctx;

    // A multi-shape selection is handled by the shared multi-select panel.
    if (isMultiShapeSelection(ctx)) return null;

    // The inspector edits the first selected arc (matches the other panels).
    const arc = doc.arcs.find((a) => a.id === selectedArcIds[0]);
    if (!arc) return null;

    /** Snapshot before a discrete (single-commit) edit so it can be undone. */
    const edit = (partial: Parameters<typeof updateArc>[1]) => {
        pushSnapshot();
        updateArc(arc.id, partial);
    };

    return (
        <div className="diagram-node-panel">
            <div className="field-row" style={{ alignItems: "center", gap: 6 }}>
                <strong style={{ flex: 1, minWidth: 0 }}>Arc properties</strong>
                <button
                    type="button"
                    className="generic-button"
                    onClick={() => setSelectedArcId(null)}
                    title="Close panel"
                    style={{ padding: "2px 6px" }}
                >
                    <X size={14} aria-hidden />
                </button>
            </div>

            {/* --- Geometry --- */}
            <div className="diagram-node-panel-section">
                <p className="diagram-node-panel-section-title">Geometry</p>
                <label className="field-label">
                    Label
                    <input
                        type="text"
                        value={arc.label}
                        onChange={(e) =>
                            updateArc(arc.id, { label: e.target.value })
                        }
                    />
                </label>
                {(["start", "end"] as const).map((end) => (
                    <div key={end} className="field-row field-row--two">
                        {(["x", "y"] as const).map((axis) => (
                            <label key={axis} className="field-label">
                                {`${end === "start" ? "Start" : "End"} ${axis.toUpperCase()}`}
                                <DebouncedInput
                                    type="number"
                                    step={1}
                                    value={arc[end][axis]}
                                    onChange={(v) =>
                                        edit({
                                            [end]: {
                                                ...arc[end],
                                                [axis]: num(v, arc[end][axis]),
                                            },
                                        })
                                    }
                                />
                            </label>
                        ))}
                    </div>
                ))}
            </div>

            {/* --- Bulge --- */}
            <div className="diagram-node-panel-section">
                <p className="diagram-node-panel-section-title">Bulge</p>
                <div className="field-row field-row--two">
                    <label className="field-label">
                        Side
                        <select
                            className="anchor-select"
                            value={arc.flip ? "flipped" : "normal"}
                            onChange={(e) =>
                                edit({ flip: e.target.value === "flipped" })
                            }
                        >
                            <option value="normal">Normal</option>
                            <option value="flipped">Flipped</option>
                        </select>
                    </label>
                    <label className="field-label">
                        Sweep (%)
                        <DebouncedInput
                            type="number"
                            min={1}
                            max={99}
                            step={1}
                            value={Math.round(arc.sweep * 100)}
                            onChange={(v) =>
                                edit({
                                    sweep: Math.max(
                                        0.01,
                                        Math.min(
                                            0.99,
                                            num(v, arc.sweep * 100) / 100,
                                        ),
                                    ),
                                })
                            }
                        />
                    </label>
                </div>
                <p
                    className="path-tool-meta"
                    style={{ margin: "4px 0 0", opacity: 0.7 }}
                >
                    50% is a semicircle; lower flattens toward a straight line,
                    higher bulges into a deeper loop.
                </p>
            </div>

            {/* --- Style --- */}
            <div className="diagram-node-panel-section">
                <p className="diagram-node-panel-section-title">Style</p>
                <StrokeStyleControl
                    strokeStyleId={arc.strokeStyleId}
                    strokeStyles={doc.strokeStyles}
                    onChange={(strokeStyleId) => edit({ strokeStyleId })}
                >
                    <div className="field-row field-row--two">
                        <label className="field-label">
                            Stroke
                            <input
                                type="color"
                                value={arc.color}
                                onChange={(e) =>
                                    updateArc(arc.id, { color: e.target.value })
                                }
                            />
                        </label>
                        <label className="field-label">
                            Width
                            <DebouncedInput
                                type="number"
                                min={0.1}
                                step={0.1}
                                value={arc.strokeWidth}
                                onChange={(v) =>
                                    edit({
                                        strokeWidth: Math.max(
                                            0.1,
                                            num(v, arc.strokeWidth),
                                        ),
                                    })
                                }
                            />
                        </label>
                    </div>
                    <label className="field-label">
                        Dash (e.g. "4 2", empty = solid)
                        <input
                            type="text"
                            value={arc.dash}
                            onChange={(e) =>
                                updateArc(arc.id, { dash: e.target.value })
                            }
                        />
                    </label>
                </StrokeStyleControl>
                <CheckboxField
                    checked={arc.visible}
                    onChange={(checked) =>
                        updateArc(arc.id, { visible: checked })
                    }
                    label={
                        <span
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 4,
                            }}
                        >
                            {arc.visible ? (
                                <Eye size={14} aria-hidden />
                            ) : (
                                <EyeOff size={14} aria-hidden />
                            )}
                            Visible
                        </span>
                    }
                    inline
                />
            </div>

            {/* --- Danger --- */}
            <div className="diagram-node-panel-section">
                <button
                    type="button"
                    className="generic-button"
                    onClick={() => removeArc(arc.id)}
                    title="Delete this arc"
                >
                    <Trash2 size={14} aria-hidden /> Delete arc
                </button>
            </div>
        </div>
    );
}
